import { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { useAuth } from '../contexts/AuthContext';
import { ethers } from 'ethers';
import api, { getErrorMessage } from '../services/api';

// REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH

// AIBL Logo
const LOGO = 'https://customer-assets.emergentagent.com/job_3c7ea79e-55a3-44e4-9827-1c3ee83e6f4f/artifacts/pc4k8dbt_Group%2083.png';

// Wallet logos
const METAMASK_LOGO = 'https://upload.wikimedia.org/wikipedia/commons/3/36/MetaMask_Fox.svg';
const ROBINHOOD_LOGO = '/robinhood-logo.png';

function LoginContent() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { isAuthenticated, loginWithWallet, loading } = useAuth();
  const [walletConnecting, setWalletConnecting] = useState(null); // 'metamask' | 'robinhood'
  const [googleLoading, setGoogleLoading] = useState(false);
  const [error, setError] = useState(null);

  // Check for error from auth callback
  useEffect(() => {
    const errorParam = searchParams.get('error');
    if (errorParam) {
      setError(decodeURIComponent(errorParam));
    }
  }, [searchParams]);

  // Redirect if already authenticated
  useEffect(() => {
    if (isAuthenticated && !loading) {
      navigate('/dashboard');
    }
  }, [isAuthenticated, loading, navigate]);

  // Emergent Google Auth - redirects to Emergent auth service
  const handleGoogleLogin = () => {
    setGoogleLoading(true);
    setError(null);
    
    // CRITICAL: Use window.location.origin to dynamically build redirect URL
    // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
    const redirectUrl = window.location.origin + '/dashboard';
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  // Connect wallet handler
  const connectWallet = async (walletType) => {
    setError(null);
    setWalletConnecting(walletType);

    try {
      // Check for provider
      let provider;
      
      if (walletType === 'robinhood' && window.coinbaseWalletExtension) {
        provider = new ethers.providers.Web3Provider(window.coinbaseWalletExtension);
      } else if (window.ethereum) {
        // Check if MetaMask is the selected provider
        if (walletType === 'metamask' && window.ethereum.isMetaMask) {
          provider = new ethers.providers.Web3Provider(window.ethereum);
        } else if (walletType === 'robinhood' && window.ethereum.isCoinbaseWallet) {
          provider = new ethers.providers.Web3Provider(window.ethereum);
        } else {
          // Default to whatever is available
          provider = new ethers.providers.Web3Provider(window.ethereum);
        }
      } else {
        throw new Error(`No ${walletType === 'metamask' ? 'MetaMask' : 'Robinhood'} wallet detected. Please install the extension.`);
      }

      await provider.send('eth_requestAccounts', []);
      const signer = provider.getSigner();
      const walletAddress = await signer.getAddress();

      console.log('Connected wallet:', walletAddress);

      // Step 1: Get nonce from backend (required for signature verification)
      const nonceRes = await api.getNonce();
      if (!nonceRes.success) {
        const errorMsg = getErrorMessage(nonceRes.error);
        throw new Error(errorMsg);
      }

      const { nonce, message } = nonceRes.data;
      console.log('Got nonce, requesting signature...');

      // Step 2: Sign the message with wallet
      let signature;
      try {
        signature = await signer.signMessage(message);
        console.log('Got signature');
      } catch (signErr) {
        if (signErr.code === 4001 || signErr.code === 'ACTION_REJECTED') {
          throw new Error('Signature rejected. Please sign the message to login.');
        }
        throw signErr;
      }

      // Step 3: Login with signature
      const result = await loginWithWallet(walletAddress, signature, nonce);
      if (result.success) {
        navigate('/dashboard');
      } else {
        // Handle specific error codes
        const errorMsg = getErrorMessage(result.error ? { code: result.errorCode, message: result.error } : null);
        setError(errorMsg);
      }
    } catch (err) {
      console.error('Wallet connection error:', err);
      if (err.code === 4001 || err.code === 'ACTION_REJECTED') {
        setError('Connection rejected. Please approve the connection in your wallet.');
      } else if (err.message?.includes('RATE_LIMITED') || err.code === 'RATE_LIMITED') {
        setError('Too many login attempts. Please wait a moment and try again.');
      } else {
        setError(err.message || 'Failed to connect wallet');
      }
    } finally {
      setWalletConnecting(null);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-[#07080f] flex items-center justify-center">
        <div className="animate-spin w-8 h-8 border-2 border-white/20 border-t-white rounded-full" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#07080f] relative overflow-hidden">
      {/* Animated gradient orbs */}
      <div className="absolute inset-0 overflow-hidden pointer-events-none">
        <motion.div
          className="absolute w-[600px] h-[600px] rounded-full opacity-30"
          style={{
            background: 'radial-gradient(circle, rgba(0, 82, 255, 0.15) 0%, transparent 70%)',
            top: '-20%',
            left: '-10%',
            filter: 'blur(80px)',
          }}
          animate={{ scale: [1, 1.2, 1], x: [0, 50, 0], y: [0, 30, 0] }}
          transition={{ duration: 15, repeat: Infinity, ease: 'easeInOut' }}
        />
        <motion.div
          className="absolute w-[500px] h-[500px] rounded-full opacity-20"
          style={{
            background: 'radial-gradient(circle, rgba(99, 102, 241, 0.2) 0%, transparent 70%)',
            bottom: '-15%',
            right: '-5%',
            filter: 'blur(60px)',
          }}
          animate={{ scale: [1, 1.15, 1], x: [0, -30, 0] }}
          transition={{ duration: 12, repeat: Infinity, ease: 'easeInOut', delay: 2 }}
        />
      </div>
      
      {/* Subtle grid */}
      <div 
        className="absolute inset-0 opacity-[0.015]"
        style={{
          backgroundImage: `linear-gradient(rgba(255,255,255,1) 1px, transparent 1px), linear-gradient(90deg, rgba(255,255,255,1) 1px, transparent 1px)`,
          backgroundSize: '80px 80px',
        }}
      />

      {/* Content */}
      <div className="relative z-10 min-h-screen flex flex-col items-center justify-center px-4">
        {/* Logo */}
        <motion.img 
          src={LOGO} 
          alt="AIBL" 
          className="h-8 mb-10"
          initial={{ opacity: 0, y: -20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
        />

        {/* Auth Card */}
        <motion.div
          initial={{ opacity: 0, y: 30, scale: 0.95 }}
          animate={{ opacity: 1, y: 0, scale: 1 }}
          transition={{ duration: 0.6, delay: 0.1 }}
          className="w-full max-w-[420px] relative"
        >
          {/* Glass card glow */}
          <div 
            className="absolute -inset-1 rounded-[28px] opacity-50"
            style={{
              background: 'linear-gradient(135deg, rgba(0, 82, 255, 0.2) 0%, rgba(99, 102, 241, 0.1) 50%, rgba(0, 82, 255, 0.15) 100%)',
              filter: 'blur(20px)',
            }}
          />
          
          {/* Card with glassmorphism */}
          <div 
            className="relative p-8 rounded-[24px] overflow-hidden"
            style={{
              background: 'linear-gradient(145deg, rgba(15, 17, 28, 0.9) 0%, rgba(10, 12, 20, 0.95) 100%)',
              border: '1px solid rgba(255, 255, 255, 0.08)',
              boxShadow: '0 0 0 1px rgba(255,255,255,0.03) inset, 0 25px 50px -12px rgba(0, 0, 0, 0.6), 0 0 80px rgba(0, 82, 255, 0.05)',
              backdropFilter: 'blur(40px)',
            }}
          >
            {/* Top shine */}
            <div 
              className="absolute top-0 left-0 right-0 h-px"
              style={{ background: 'linear-gradient(90deg, transparent, rgba(255,255,255,0.1), transparent)' }}
            />

            {/* Header */}
            <div className="text-center mb-8">
              <h1 className="text-2xl font-semibold text-white mb-2">Welcome to AIBL</h1>
              <p className="text-white/45 text-sm">AI Banking Layer Dashboard</p>
            </div>

            {/* Error message */}
            <AnimatePresence>
              {error && (
                <motion.div
                  initial={{ opacity: 0, height: 0, marginBottom: 0 }}
                  animate={{ opacity: 1, height: 'auto', marginBottom: 24 }}
                  exit={{ opacity: 0, height: 0, marginBottom: 0 }}
                  className="p-3 rounded-xl bg-red-500/10 border border-red-500/20 overflow-hidden"
                >
                  <p className="text-red-400 text-sm text-center">{error}</p>
                </motion.div>
              )}
            </AnimatePresence>

            {/* Auth Buttons */}
            <div className="space-y-3">
              {/* Google Login via Emergent Auth */}
              <motion.button
                onClick={handleGoogleLogin}
                disabled={googleLoading}
                className="w-full flex items-center justify-center gap-3 px-5 py-4 rounded-xl font-medium text-white transition-all duration-300 disabled:opacity-50 disabled:cursor-not-allowed group relative overflow-hidden"
                style={{
                  background: 'linear-gradient(135deg, rgba(234, 67, 53, 0.15) 0%, rgba(234, 67, 53, 0.08) 100%)',
                  border: '1px solid rgba(234, 67, 53, 0.3)',
                }}
                whileHover={{ scale: 1.01 }}
                whileTap={{ scale: 0.99 }}
                data-testid="google-login-btn"
              >
                <motion.div 
                  className="absolute inset-0 opacity-0 group-hover:opacity-100 transition-opacity duration-300"
                  style={{ background: 'linear-gradient(135deg, rgba(234, 67, 53, 0.25) 0%, rgba(234, 67, 53, 0.15) 100%)' }}
                />
                {googleLoading ? (
                  <>
                    <div className="animate-spin w-5 h-5 border-2 border-white/20 border-t-white rounded-full relative z-10" />
                    <span className="relative z-10">Redirecting...</span>
                  </>
                ) : (
                  <>
                    <svg width="20" height="20" viewBox="0 0 24 24" className="relative z-10">
                      <path fill="#EA4335" d="M24 5.457v13.909c0 .904-.732 1.636-1.636 1.636h-3.819V11.73L12 16.64l-6.545-4.91v9.273H1.636A1.636 1.636 0 0 1 0 19.366V5.457c0-2.023 2.309-3.178 3.927-1.964L5.455 4.64 12 9.548l6.545-4.91 1.528-1.145C21.69 2.28 24 3.434 24 5.457z"/>
                    </svg>
                    <span className="relative z-10">Continue with Google</span>
                  </>
                )}
              </motion.button>

              {/* Divider */}
              <div className="flex items-center gap-4 py-2">
                <div className="flex-1 h-px bg-gradient-to-r from-transparent via-white/10 to-transparent" />
                <span className="text-white/25 text-xs uppercase tracking-wider font-medium">or connect wallet</span>
                <div className="flex-1 h-px bg-gradient-to-r from-transparent via-white/10 to-transparent" />
              </div>

              {/* MetaMask */}
              <motion.button
                onClick={() => connectWallet('metamask')}
                disabled={walletConnecting !== null}
                className="w-full flex items-center justify-center gap-3 px-5 py-4 rounded-xl font-medium text-white transition-all duration-300 disabled:opacity-50 disabled:cursor-not-allowed group relative overflow-hidden"
                style={{
                  background: 'linear-gradient(135deg, rgba(246, 133, 27, 0.12) 0%, rgba(246, 133, 27, 0.06) 100%)',
                  border: '1px solid rgba(246, 133, 27, 0.25)',
                }}
                whileHover={{ scale: 1.01 }}
                whileTap={{ scale: 0.99 }}
                data-testid="metamask-login-btn"
              >
                <motion.div 
                  className="absolute inset-0 opacity-0 group-hover:opacity-100 transition-opacity duration-300"
                  style={{ background: 'linear-gradient(135deg, rgba(246, 133, 27, 0.2) 0%, rgba(246, 133, 27, 0.1) 100%)' }}
                />
                {walletConnecting === 'metamask' ? (
                  <>
                    <div className="animate-spin w-5 h-5 border-2 border-white/20 border-t-white rounded-full relative z-10" />
                    <span className="relative z-10">Connecting...</span>
                  </>
                ) : (
                  <>
                    <img src={METAMASK_LOGO} alt="MetaMask" className="w-6 h-6 relative z-10" />
                    <span className="relative z-10">MetaMask</span>
                  </>
                )}
              </motion.button>

              {/* Robinhood Wallet */}
              <motion.button
                onClick={() => connectWallet('robinhood')}
                disabled={walletConnecting !== null}
                className="w-full flex items-center justify-center gap-3 px-5 py-4 rounded-xl font-medium text-white transition-all duration-300 disabled:opacity-50 disabled:cursor-not-allowed group relative overflow-hidden"
                style={{
                  background: 'linear-gradient(135deg, rgba(0, 200, 5, 0.12) 0%, rgba(0, 200, 5, 0.06) 100%)',
                  border: '1px solid rgba(0, 200, 5, 0.25)',
                }}
                whileHover={{ scale: 1.01 }}
                whileTap={{ scale: 0.99 }}
                data-testid="robinhood-login-btn"
              >
                <motion.div 
                  className="absolute inset-0 opacity-0 group-hover:opacity-100 transition-opacity duration-300"
                  style={{ background: 'linear-gradient(135deg, rgba(0, 200, 5, 0.2) 0%, rgba(0, 200, 5, 0.1) 100%)' }}
                />
                {walletConnecting === 'robinhood' ? (
                  <>
                    <div className="animate-spin w-5 h-5 border-2 border-white/20 border-t-white rounded-full relative z-10" />
                    <span className="relative z-10">Connecting...</span>
                  </>
                ) : (
                  <>
                    <img src={ROBINHOOD_LOGO} alt="Robinhood" className="w-6 h-6 rounded relative z-10" />
                    <span className="relative z-10">Robinhood Wallet</span>
                  </>
                )}
              </motion.button>
            </div>

            {/* Terms */}
            <p className="text-white/30 text-xs text-center mt-6 leading-relaxed">
              By continuing, you agree to our{' '}
              <a href="/terms" className="text-white/50 hover:text-white/70 underline underline-offset-2">Terms</a>
              {' '}and{' '}
              <a href="/privacy" className="text-white/50 hover:text-white/70 underline underline-offset-2">Privacy Policy</a>
            </p>
          </div>
        </motion.div>

        {/* Back to home */}
        <motion.a
          href="/"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.4 }}
          className="mt-10 text-white/35 text-sm hover:text-white/55 transition-colors flex items-center gap-2"
        >
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <path d="M19 12H5M12 19l-7-7 7-7"/>
          </svg>
          Back to home
        </motion.a>
      </div>
    </div>
  );
}

export default function LoginPage() {
  return <LoginContent />;
}
